import type { JSX } from 'react';

import { Translate } from '@change-corgi/core/react/i18n';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { Flex } from '@change-corgi/design-system-legacy/layout';
import { useIsSunriseTheme } from '@change-corgi/design-system-legacy/theme';
import { Heading } from '@change-corgi/design-system-legacy/typography';

import { SectionSeparator } from 'src/app/pages/petitionDetails/shared/components';

import { DecisionMakerCardContainer } from './components/DecisionMakerCardContainer';
import { useDecisionMakerCards } from './hooks/useDecisionMakerCards';

export function DecisionMakerCardsContainer(): JSX.Element | null {
	const {
		data: { displayedDMs, displayShowAllButton, allDmResponses, remainingDMsCount },
		actions: { handleViewMoreClick },
	} = useDecisionMakerCards();

	const isSunriseThemeEnabled = useIsSunriseTheme();

	if (displayedDMs.length === 0) return null;

	const listMargin = isSunriseThemeEnabled ? 32 : 24;

	return (
		<>
			<SectionSeparator />

			<Flex sx={{ flexDirection: 'column', gap: 24 }} data-testid="decision-maker-cards">
				<Heading as="h2" size={['h3', 'h2']} id="decision-makers-heading">
					<Translate value="corgi.petition_gamma.decision_makers.section_title" />
				</Heading>

				{/* using role due to https://www.scottohara.me/blog/2019/01/12/lists-and-safari.html */}
				{/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
				<ul role="list">
					{displayedDMs.map((decisionMaker, index) => (
						<li key={decisionMaker.id} sx={{ mb: index === displayedDMs.length - 1 ? 0 : listMargin }}>
							<DecisionMakerCardContainer
								key={decisionMaker.id}
								decisionMaker={decisionMaker}
								allDmResponses={allDmResponses}
							/>
						</li>
					))}
				</ul>

				{displayShowAllButton && (
					<Button
						variant="secondary"
						sx={{ width: '100%' }}
						onClick={handleViewMoreClick}
						data-qa="view-more-dms"
						data-testid="view-more-dms"
					>
						<Translate
							value="corgi.petition_gamma.decision_makers.view_more"
							replacements={{ numberOfDms: remainingDMsCount }}
						/>
					</Button>
				)}
			</Flex>
		</>
	);
}
